import {
  FREE_PLAN_CAPTURE_LIMIT,
  PROFESSIONAL_MONTHLY_CAPTURE_LIMIT,
  STUDIO_MONTHLY_CAPTURE_LIMIT,
} from './uploadLimits';
import {
  EXTRA_SEAT_PRICE_USD_PROFESSIONAL,
  EXTRA_SEAT_PRICE_USD_STUDIO,
  INCLUDED_SEATS_PROFESSIONAL,
  INCLUDED_SEATS_STUDIO,
  WORKSPACE_SEATS_ENABLED,
} from './seatPricing';

/**
 * Shared plan bullet structure for Pricing + Studio Billing.
 * @typedef {{ text: string, icon?: 'check' | 'plus' | 'none' | 'storage' | 'users', indent?: boolean, bold?: boolean }} PlanFeatureLine
 * indent is only applied when icon is 'plus' (video duration sub-bullets).
 */

/** @param {'free'|'professional'|'studio'|'enterprise'} planId */
export function getPlanSubtitle(planId) {
  switch (planId) {
    case 'free':
      return 'Try Volugraph on your first projects';
    case 'professional':
      return 'For individual professionals';
    case 'studio':
      return 'For teams and studios';
    case 'enterprise':
      return 'For large-scale operations';
    default:
      return '';
  }
}

/** @param {'free'|'professional'|'studio'|'enterprise'} planId @returns {PlanFeatureLine[]} */
export function seatFeatureLines(planId) {
  if (!WORKSPACE_SEATS_ENABLED) return [];
  switch (planId) {
    case 'free':
      return [];
    case 'professional':
      return [{
        text: `${INCLUDED_SEATS_PROFESSIONAL} seat (extra seats $${EXTRA_SEAT_PRICE_USD_PROFESSIONAL}/month each)`,
        icon: 'users',
      }];
    case 'studio':
      return [{
        text: `${INCLUDED_SEATS_STUDIO} seats included (extra seats $${EXTRA_SEAT_PRICE_USD_STUDIO}/month each)`,
        icon: 'users',
      }];
    case 'enterprise':
      return [{ text: 'Unlimited seats', icon: 'users' }];
    default:
      return [];
  }
}

/** @returns {PlanFeatureLine[]} */
export function getFreePlanFeatures() {
  return [
    { text: `${FREE_PLAN_CAPTURE_LIMIT} Free Captures`, icon: 'check', bold: true },
    { text: 'Up to 1 minute per video upload', icon: 'plus', indent: true },
    { text: 'Shareable Public Links', icon: 'check' },
    { text: 'Full 3D Editing Workspace Features', icon: 'check' },
    ...seatFeatureLines('free'),
  ];
}

/** @returns {PlanFeatureLine[]} */
export function getProfessionalPlanFeatures() {
  return [
    { text: 'Everything in Free +', icon: 'none', bold: true },
    { text: `${PROFESSIONAL_MONTHLY_CAPTURE_LIMIT} Captures Per Month`, icon: 'check', bold: true },
    { text: 'Up to 5 minutes per video upload', icon: 'plus', indent: true },
    { text: 'Password-protected links & .PLY downloads', icon: 'check' },
    { text: 'Team Collaboration', icon: 'check' },
    { text: '50GB Storage', icon: 'storage' },
    ...seatFeatureLines('professional'),
  ];
}

/** @returns {PlanFeatureLine[]} */
export function getStudioPlanFeatures() {
  return [
    { text: 'Everything in Professional +', icon: 'none', bold: true },
    { text: `${STUDIO_MONTHLY_CAPTURE_LIMIT} Captures Per Month`, icon: 'check', bold: true },
    { text: 'Up to 10 minutes per video upload', icon: 'plus', indent: true },
    { text: '100GB Storage', icon: 'storage' },
    ...seatFeatureLines('studio'),
  ];
}

/** @returns {PlanFeatureLine[]} */
export function getEnterprisePlanFeatures() {
  return [
    { text: 'Custom integrations and tooling for large-scale deployments.', icon: 'check' },
    ...seatFeatureLines('enterprise'),
  ];
}
